import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { isCrmCoreRole } from '@crm/shared'
import { getClient, updateClient, deleteClient } from '../../shared/api/clients.api'
import { useAuthStore } from '../auth/authStore'
import { PageLoader } from '../../shared/ui/Spinner'
import { CategoryBadge } from '../../shared/ui/Badge'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { confirm } from '../../shared/ui/confirmStore'
import { ClientRequestsTable } from './ClientRequestsTable'
import { ReassignOwnerModal } from './ReassignOwnerModal'
import type { ClientCategory, ClientStats } from '../../shared/api/types'
import { normalizeCity, SEGMENT_OPTIONS } from '../../shared/utils/clientFields'
import { askSaveDespiteDuplicateInn } from './duplicateInn'

const CLOSED_REQUEST_STATUSES = ['CLOSED_WON', 'CLOSED_LOST', 'ARCHIVED']

// ─── Metric card ──────────────────────────────────────────────────────────────

function MetricCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
      <div className="text-xs text-gray-500 mt-1 leading-tight">{label}</div>
    </div>
  )
}

function ClientMetrics({
  clientId,
  stats,
  canOpenProjects,
}: {
  clientId: string
  stats?: ClientStats
  canOpenProjects: boolean
}) {
  if (!stats) return null
  const fmtRub = (v: number) =>
    v > 0 ? v.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽' : '—'
  const amount = fmtRub(stats.totalAmount)
  const ordered = fmtRub(stats.orderedAmount ?? 0)
  // Проекты (Р12): KPI-карточка вместо плоского списка (решение заказчика 14.07,
  // «иначе запутаемся»). Клик ведёт на отдельный экран списка проектов.
  const hasProjects = (stats.projectsCount ?? 0) > 0
  const projectsValue =
    stats.projectsAmount > 0
      ? `${stats.projectsCount} · ${fmtRub(stats.projectsAmount)}`
      : String(stats.projectsCount)
  return (
    <div>
      <h2 className="text-base font-medium text-gray-900 mb-3">Показатели</h2>
      <div
        className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${hasProjects ? 'lg:grid-cols-6' : 'lg:grid-cols-5'}`}
      >
        <MetricCard label="Всего заявок" value={stats.totalRequests} />
        <MetricCard label="КП отправлено" value={stats.quoteSent} />
        <MetricCard label="Сумма в заказ" value={ordered} />
        <MetricCard label="Оплачено (WON)" value={stats.closedWon} />
        <MetricCard label="Сумма по КП" value={amount} />
        {hasProjects && !canOpenProjects && <MetricCard label="Проекты" value={projectsValue} />}
        {hasProjects && canOpenProjects && (
          <Link
            to={`/clients/${clientId}/projects`}
            className="bg-white rounded-xl border border-gray-200 p-4 transition-colors hover:border-brand-500"
            title="Открыть список проектов клиента"
          >
            <div className="text-2xl font-bold text-brand-600 tabular-nums">{projectsValue}</div>
            <div className="text-xs text-gray-500 mt-1 leading-tight">Проекты →</div>
          </Link>
        )}
      </div>
    </div>
  )
}

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const canEdit =
    user?.role === 'ADMIN' ||
    user?.role === 'MANAGER' ||
    user?.role === 'ROP' ||
    user?.role === 'ASSISTANT' ||
    user?.role === 'ACCOUNTANT'
  // Бухгалтер правит клиента (ИНН, код, реквизиты), но не удаляет — 14.10.2026.
  const canDelete = canEdit && user?.role !== 'ACCOUNTANT'
  // Сменить владельца клиента могут только ADMIN/ROP (см. бэк).
  const canReassign = user?.role === 'ADMIN' || user?.role === 'ROP'
  // Бухгалтер (этап 3, Б1) карточку только смотрит: заявки и «Проекты» ему закрыты.
  const canOpenRequests = isCrmCoreRole(user?.role ?? '')
  const [reassigning, setReassigning] = useState(false)

  // ── Edit state ──────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editCode, setEditCode] = useState('')
  const [editCategory, setEditCategory] = useState<ClientCategory>('B')
  const [editNotes, setEditNotes] = useState('')
  const [editInn, setEditInn] = useState('')
  const [editCity, setEditCity] = useState('')
  const [editSegment, setEditSegment] = useState('')
  const [saveError, setSaveError] = useState<unknown>(null)
  const [deleteError, setDeleteError] = useState<unknown>(null)

  const { data: client, isLoading } = useQuery({
    queryKey: ['clients', id],
    queryFn: () => getClient(id!),
    enabled: !!id,
  })

  const openEdit = () => {
    if (!client) return
    setEditName(client.name)
    setEditCode(client.code ?? '')
    setEditCategory(client.category)
    setEditNotes(client.notes ?? '')
    setEditInn(client.inn ?? '')
    setEditCity(client.city ?? '')
    setEditSegment(client.segment ?? '')
    setSaveError(null)
    setEditing(true)
  }

  const cancelEdit = () => setEditing(false)

  const saveMutation = useMutation({
    mutationFn: (confirmDuplicateInn: boolean) =>
      updateClient(id!, {
        name: editName.trim(),
        code: editCode.trim() || undefined,
        category: editCategory,
        notes: editNotes.trim() || undefined,
        inn: editInn.trim() || null,
        city: normalizeCity(editCity) || null,
        segment: editSegment || null,
        ...(confirmDuplicateInn ? { confirmDuplicateInn: true } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients', id] })
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      setEditing(false)
    },
  })

  // Такой ИНН уже есть у другого клиента — сервер отвечает 409 DUPLICATE_INN;
  // после подтверждения повторяем с флагом (решение Сергея 14.09.2026).
  const handleSave = async (confirmDuplicateInn = false) => {
    setSaveError(null)
    try {
      await saveMutation.mutateAsync(confirmDuplicateInn)
    } catch (err) {
      if (!confirmDuplicateInn && (await askSaveDespiteDuplicateInn(err))) {
        await handleSave(true)
        return
      }
      setSaveError(err)
    }
  }

  const deleteMutation = useMutation({
    mutationFn: () => deleteClient(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      void navigate('/clients')
    },
    onError: setDeleteError,
  })

  const handleDelete = async () => {
    if (
      !(await confirm({
        title: `Удалить клиента «${client?.name}»?`,
        message: 'Это действие нельзя отменить.',
        danger: true,
        confirmText: 'Удалить',
      }))
    )
      return
    setDeleteError(null)
    deleteMutation.mutate()
  }

  const canSave = editName.trim().length >= 2 && !saveMutation.isPending

  if (isLoading) return <PageLoader />
  if (!client) return <p className="text-gray-500 text-sm">Клиент не найден</p>

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="text-sm text-gray-500">
        <Link to="/clients" className="hover:text-gray-900">
          Клиенты
        </Link>
        <span className="mx-2">›</span>
        <span className="text-gray-900">{client.name}</span>
      </div>

      {/* Header card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {editing ? (
          /* ── Режим редактирования ── */
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-medium text-gray-700">Редактирование клиента</h2>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Название <span className="text-red-500">*</span>
              </label>
              <input
                autoFocus
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Код клиента</label>
                <input
                  type="text"
                  value={editCode}
                  onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                  placeholder="ROG001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Категория</label>
                <select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value as ClientCategory)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
                >
                  <option value="A">A — Ключевой заказчик</option>
                  <option value="B">B — Растущий клиент</option>
                  <option value="C">C — Новый заказчик</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">ИНН</label>
                <input
                  type="text"
                  value={editInn}
                  onChange={(e) => setEditInn(e.target.value.replace(/\D/g, ''))}
                  placeholder="7712345678"
                  maxLength={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Город</label>
                <input
                  type="text"
                  value={editCity}
                  onChange={(e) => setEditCity(e.target.value)}
                  onBlur={(e) => {
                    const norm = normalizeCity(e.target.value)
                    if (norm) setEditCity(norm)
                  }}
                  placeholder="Москва"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Сегмент</label>
              <select
                value={editSegment}
                onChange={(e) => setEditSegment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
              >
                <option value="">— не выбран —</option>
                {SEGMENT_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Заметки</label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={2}
                placeholder="Необязательно..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
              />
            </div>

            {saveError != null && <ErrorMessage error={saveError} />}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={!canSave}
                className="px-5 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {saveMutation.isPending ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="px-5 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              >
                Отмена
              </button>
            </div>
          </div>
        ) : (
          /* ── Режим просмотра ── */
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{client.name}</h1>
                {client.code && (
                  <p className="text-sm text-gray-500 font-mono mt-0.5">{client.code}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <CategoryBadge category={client.category} />
                {canEdit && (
                  <button
                    type="button"
                    onClick={openEdit}
                    className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Изменить
                  </button>
                )}
                {canReassign && (
                  <button
                    type="button"
                    onClick={() => setReassigning(true)}
                    className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Сменить владельца
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
                  >
                    {deleteMutation.isPending ? 'Удаление...' : 'Удалить'}
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Менеджер:</span>{' '}
                <span className="text-gray-900">
                  {client.manager?.name || client.manager?.email || '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Создан:</span>{' '}
                <span className="text-gray-900">
                  {new Date(client.createdAt).toLocaleDateString('ru-RU')}
                </span>
              </div>
              {client.inn && (
                <div>
                  <span className="text-gray-500">ИНН:</span>{' '}
                  <span className="text-gray-900 font-mono">{client.inn}</span>
                </div>
              )}
              {client.city && (
                <div>
                  <span className="text-gray-500">Город:</span>{' '}
                  <span className="text-gray-900">{client.city}</span>
                </div>
              )}
              {client.segment && (
                <div>
                  <span className="text-gray-500">Сегмент:</span>{' '}
                  <span className="text-gray-900">{client.segment}</span>
                </div>
              )}
              {client.notes && (
                <div className="col-span-2">
                  <span className="text-gray-500">Заметки:</span>{' '}
                  <span className="text-gray-900">{client.notes}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {deleteError != null && <ErrorMessage error={deleteError} />}

      {/* Показатели (включая кликабельную карточку «Проекты» → /clients/:id/projects) */}
      <ClientMetrics clientId={client.id} stats={client.stats} canOpenProjects={canOpenRequests} />

      {/* Requests */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-medium text-gray-900">Заявки</h2>
          {canOpenRequests && (
            <Link
              to={`/requests/new?clientId=${client.id}`}
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
            >
              + Новая заявка
            </Link>
          )}
        </div>

        {!client.requests?.length ? (
          <EmptyState title="Заявок пока нет" />
        ) : (
          <ClientRequestsTable clientId={client.id} requests={client.requests} />
        )}
      </div>

      {reassigning && (
        <ReassignOwnerModal
          clientId={client.id}
          clientName={client.name}
          currentOwnerId={client.managerId ?? ''}
          activeRequestsCount={
            (client.requests ?? []).filter((r) => !CLOSED_REQUEST_STATUSES.includes(r.status))
              .length
          }
          onClose={() => setReassigning(false)}
        />
      )}
    </div>
  )
}
